// File: packages/theme/src/tokens.ts
// Version: 2026.4.1 | ForgeTrack Engine design tokens

/** Accent palette — each color has a specific semantic meaning */
export const ENGINE_COLORS = {
  // Accent palette
  neon:    '#34d399',  // Primary engine green — success, active, live
  cyan:    '#22d3ee',  // Data, connectivity, simulation
  violet:  '#a78bfa',  // Analysis, intelligence, deep thinking
  amber:   '#fbbf24',  // Urgency, warnings, attention required
  coral:   '#f97066',  // Danger, risk, negative impact
  blue:    '#60a5fa',  // Timeline, scheduling, neutral data
  pink:    '#f472b6',  // Reserved for accent pops

  // Text hierarchy
  text:     '#e8ecf2',
  textSoft: 'rgba(255,255,255,0.78)',
  muted:    'rgba(255,255,255,0.6)',
  dim:      'rgba(255,255,255,0.42)',
  faint:    'rgba(255,255,255,0.15)',

  // Surfaces — layered depth
  bg:        '#050910',
  surface:   'rgba(8,16,32,0.88)',
  panel:     'rgba(12,22,44,0.92)',
  cardHover: 'rgba(16,28,52,0.95)',

  // Glow variants (for textShadow and boxShadow)
  neonGlow:   'rgba(52,211,153,0.6)',
  cyanGlow:   'rgba(34,211,238,0.5)',
  violetGlow: 'rgba(167,139,250,0.5)',
  amberGlow:  'rgba(251,191,36,0.5)',
  coralGlow:  'rgba(249,112,102,0.5)',
} as const;

export const ENGINE_FONTS = {
  display: "'Outfit', system-ui, -apple-system, sans-serif",
  mono:    "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace",
} as const;

/** Signature ease curve — fast start, smooth deceleration, slight overshoot */
export const ENGINE_EASE = [0.16, 1, 0.3, 1] as const;

/** CSS keyframes injected once per page via EnginePageShell */
export const ENGINE_KEYFRAMES = `
@keyframes engine-neural {
  0% { background-position: 0 0; }
  100% { background-position: 60px 60px; }
}
@keyframes engine-scan {
  0% { top: -100%; }
  100% { top: 200%; }
}
@keyframes engine-core-pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
}
@keyframes engine-ping {
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(2.5); opacity: 0; }
}
@keyframes engine-feed-in {
  0% { opacity: 0; transform: translateX(-12px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes engine-icon-pop {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes engine-icon-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes engine-icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}
@keyframes engine-icon-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes engine-icon-bounce {
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-6px); }
  60% { transform: translateY(-3px); }
}
@keyframes engine-reticle {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes engine-data-stream {
  0% { opacity: 0; transform: scale(0.5); }
  50% { opacity: 0.8; }
  100% { opacity: 0; transform: scale(1.5); }
}
@keyframes engine-tooltip-in {
  0% { opacity: 0; transform: translateY(4px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
`;

export type EngineColor = keyof typeof ENGINE_COLORS;
export type EngineFont = keyof typeof ENGINE_FONTS;
